import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDropdownPlacement } from '../../../hooks/useDropdownPlacement';
import type { Props, SearchFilterSelectOption } from './types';

// SearchFilterDropdown の単一選択版。
// ブラウザ標準の <select> ではなく、検索フォーム内で使う独自パネルUIで単一選択させる。
export const Presentation: FunctionComponent<Props> = ({
  options,
  value,
  onChange,
  placeholder = '指定なし',
  disabled = false,
  ariaLabel,
  flip = true,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { panelRef, panelStyle } = useDropdownPlacement(open, rootRef, { flip });

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      // パネルは body へ Portal されるため rootRef には含まれない。両方を除外する
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // disabled に切り替わったら開いたままにしない
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const selectedLabel = options.find((option) => option.value === value)?.label;
  // 先頭に解除用（value 空文字）の行を差し込む
  const rows: SearchFilterSelectOption[] = [{ value: '', label: placeholder }, ...options];

  const handleSelect = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className="search-form__dropdown" ref={rootRef}>
      <button
        type="button"
        className={`search-form__dropdown__trigger${open ? ' is-open' : ''}`}
        aria-label={ariaLabel}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={`search-form__dropdown__trigger__label${selectedLabel ? '' : ' placeholder'}`}>
          {selectedLabel || placeholder}
        </span>
        <svg className={`search-form__dropdown__trigger__arrow${open ? ' is-open' : ''}`} xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="267.69 -596.31 424.62 235.62" width="10px" fill="#19190F"><path d="M480-360.69 267.69-573 291-596.31l189 189 189-189L692.31-573 480-360.69Z"/></svg>
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          className={`search-form__dropdown__panel${flip ? '' : ' search-form__dropdown__panel--fit'}`}
          style={panelStyle}
        >
          <div className="search-form__dropdown__panel__contents">
            <ul className="padding-0">
              {rows.map((option) => {
                const selected = option.value === value;
                const optionDisabled = Boolean(option.disabled);
                return (
                  <li
                    key={option.value || '__none__'}
                    className={`${selected ? 'selected' : ''}${optionDisabled ? ' disabled' : ''}`.trim()}
                  >
                    <label>
                      {option.label}
                      <input
                        type="radio"
                        value={option.value}
                        checked={selected}
                        disabled={optionDisabled}
                        onChange={() => {
                          if (optionDisabled) return;
                          handleSelect(option.value);
                        }}
                        className="checkbox-input"
                      />
                      {selected && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="10" viewBox="0 0 14 10" fill="none"><path d="M4.88542 9.89714L0.21875 5.23047L1.11458 4.33464L4.88542 8.10547L12.8854 0.105469L13.7812 1.0013L4.88542 9.89714Z" fill="#19190F" /></svg>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};
